import { useCallback, useEffect, useId, useState, type ChangeEvent } from 'react';
import { MobileLegalLinks } from '../widget-ui/mobile-legal-links';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { Button } from '@domcrm/embed-ui';

import { getMe, patchMeProfile } from '../widget-api/auth-me';
import {
  getAuthContexts,
  postAuthSwitchContext,
  type LoginContextOption
} from '../widget-api/auth-contexts';
import { postSelectTrainerBranch } from '../widget-api/trainer-branch';
import { ApiError } from '../widget-api/http-client';
import { uploadMediaWithToken } from '../widget-api/media-upload';
import { useEmbedBootstrap } from '../use-widget-bootstrap';
import { useTrainerBranchOptions } from '../widget-hooks/useTrainerBranchOptions';
import { useQuerySuffix } from '../widget-hooks/useQuerySuffix';
import { useWidgetSeo } from '../widget-hooks/use-widget-seo';
import { isOrgStaffLineWithRuntime } from '../widget-lib/fitness-trainer-persona';
import { clearEmbedSession, clearGlobalEmbedSession, getEmbedSession, setEmbedSession } from '../widget-lib/embed-session-store';
import { displayableMediaUrl } from '../widget-lib/media-url';
import { PATH } from '../widget-lib/path-routes';
import { setSuperadminOrgId } from '../widget-lib/superadmin-org';
import { EmbedInput } from '../widget-ui/embed-input';
import { EmbedMessageBlock } from '../widget-ui/embed-message-block';
import { ImageCropModal } from '../widget-ui/image-crop-modal';
import { TelegramLinkCard } from '../widget-ui/telegram-link-card';
import { DeleteAccountDialog } from '../widget-ui/delete-account-dialog';
import { LeaveOrganizationDialog } from '../widget-ui/leave-organization-dialog';

function IconPencil() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
      <path d="m15 5 4 4" />
    </svg>
  );
}

export default function TrainerProfilePage() {
  useWidgetSeo(
    'Профиль тренера',
    'Личный профиль и настройки кабинета тренера.'
  );
  const suffix = useQuerySuffix();
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const { me, meLoading, meError, accessToken, reloadMe, orgSlug, widgetContour } = useEmbedBootstrap();
  const { branchOptions, allOrgBranches } = useTrainerBranchOptions(me);
  const avatarInputId = useId();
  const nameFieldId = useId();

  const [nameOpen, setNameOpen] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [nameSaving, setNameSaving] = useState(false);
  const [nameErr, setNameErr] = useState<string | null>(null);

  const [contextsOpen, setContextsOpen] = useState(false);
  const [contexts, setContexts] = useState<LoginContextOption[]>([]);
  const [contextsLoading, setContextsLoading] = useState(false);
  const [contextsError, setContextsError] = useState<string | null>(null);
  const [contextSwitchId, setContextSwitchId] = useState<string | null>(null);

  const [cropOpen, setCropOpen] = useState(false);
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [avatarErr, setAvatarErr] = useState<string | null>(null);

  const [branchBusy, setBranchBusy] = useState(false);
  const [branchErr, setBranchErr] = useState<string | null>(null);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [leaveOpen, setLeaveOpen] = useState(false);

  const handleAccountDeleted = useCallback(() => {
    clearEmbedSession(orgSlug);
    clearGlobalEmbedSession();
    const params = new URLSearchParams(suffix.startsWith('?') ? suffix.slice(1) : suffix);
    params.set('account_deleted', '1');
    window.location.href = `/embed/trainer/sign-in?${params.toString()}`;
  }, [orgSlug, suffix]);

  const chooseBranch = useCallback(async (branchId: number) => {
    const slug = orgSlug || me?.memberships?.find((m) => m.organization_id === me.organization_id)?.organization_slug || null;
    if (!slug || !accessToken) return;
    setBranchBusy(true);
    setBranchErr(null);
    try {
      const currentSession = getEmbedSession(slug);
      await postSelectTrainerBranch(branchId, accessToken);
      const fresh = await getMe(accessToken);
      setEmbedSession(slug, {
        accessToken: accessToken,
        organizationId: fresh.organization_id!,
        embedMode: 'trainer',
        refreshToken: currentSession?.refreshToken
      });
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('org_slug', slug);
        return next;
      });
      await reloadMe();
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        clearEmbedSession(slug);
        clearGlobalEmbedSession();
        window.location.reload();
        return;
      }
      const msg = e instanceof ApiError ? e.detail : e instanceof Error ? e.message : 'Ошибка';
      setBranchErr(msg);
    } finally {
      setBranchBusy(false);
    }
  }, [orgSlug, me, accessToken, reloadMe, setSearchParams]);

  useEffect(() => {
    if (!me || meLoading || allOrgBranches) return;
    const placements = me.trainer_placements ?? [];
    if (me.active_branch_id == null && placements.length === 1) {
      void chooseBranch(placements[0].branch_id);
    }
  }, [me, meLoading, allOrgBranches, chooseBranch]);

  const openContexts = useCallback(async () => {
    if (!accessToken) return;
    setContextsOpen(true);
    setContextsLoading(true);
    setContextsError(null);
    try {
      const res = await getAuthContexts(accessToken);
      setContexts(res.available ?? []);
    } catch (e) {
      setContexts([]);
      setContextsError(e instanceof ApiError ? e.detail : e instanceof Error ? e.message : 'Не удалось загрузить роли.');
    } finally {
      setContextsLoading(false);
    }
  }, [accessToken]);

  const switchToContext = useCallback(
    async (context: LoginContextOption) => {
      if (!accessToken) return;
      setContextSwitchId(context.id);
      setContextsError(null);
      try {
        const tokens = await postAuthSwitchContext(context.id, accessToken);
        setSuperadminOrgId(null);
        const fresh = await getMe(tokens.access_token);
        const oid = fresh.organization_id;
        if (oid == null) {
          // Платформенный контекст суперадмина: сначала выбор студии.
          setEmbedSession(orgSlug || 'platform', {
            accessToken: tokens.access_token,
            organizationId: null,
            embedMode: 'trainer',
            refreshToken: tokens.refresh_token ?? undefined
          });
          setContextsOpen(false);
          navigate(`${PATH.trainerSelectStudio}${suffix}`, { replace: true });
          return;
        }
        const slug =
          fresh.organizations?.find((o) => o.id === oid)?.slug?.trim() ||
          fresh.memberships?.find((m) => m.organization_id === oid)?.organization_slug?.trim() ||
          `org_${oid}`;
        setEmbedSession(slug, {
          accessToken: tokens.access_token,
          organizationId: oid,
          embedMode: 'trainer',
          refreshToken: tokens.refresh_token ?? undefined
        });
        setContextsOpen(false);
        // Контекст сменился — в URL должен уйти новый org_slug, иначе bootstrap
        // прочитает старую студию и приложение останется в прежней организации.
        const next = new URLSearchParams(suffix.startsWith('?') ? suffix.slice(1) : suffix);
        next.set('org_slug', slug);
        navigate(`${PATH.trainer}?${next.toString()}`, { replace: true });
      } catch (e) {
        setContextsError(e instanceof ApiError ? e.detail : e instanceof Error ? e.message : 'Не удалось сменить роль.');
      } finally {
        setContextSwitchId(null);
      }
    },
    [accessToken, navigate, orgSlug, suffix]
  );

  const closeCrop = useCallback(() => {
    setCropOpen(false);
    setCropSrc((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  }, []);

  function onAvatarFile(ev: ChangeEvent<HTMLInputElement>): void {
    const file = ev.target.files?.[0];
    ev.target.value = '';
    if (!file || !file.type.startsWith('image/')) return;
    setCropSrc((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
    setCropOpen(true);
  }

  function openNameDialog(): void {
    setNameDraft(me?.full_name?.trim() ?? '');
    setNameErr(null);
    setNameOpen(true);
  }

  if (meLoading) {
    return (
      <div className="space-y-4 w-full min-w-0">
        <div className="h-7 w-24 animate-pulse rounded bg-[var(--bg-soft)]" />
        <div className="grid gap-3 sm:grid-cols-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="space-y-1">
              <div className="h-3 w-20 animate-pulse rounded bg-[var(--bg-soft)]" />
              <div className="h-4 w-36 animate-pulse rounded bg-[var(--bg-soft)]" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (meError) {
    return (
      <div className="space-y-2 w-full min-w-0">
        <h1 className="text-xl font-semibold tracking-tight">Профиль</h1>
        <p className="text-sm text-[var(--error-text)]">{meError}</p>
      </div>
    );
  }

  if (!me || !accessToken) {
    return (
      <div className="w-full min-w-0">
        <p className="text-sm text-[var(--text-muted)]">Нет данных профиля.</p>
      </div>
    );
  }


  const avatarUrl = displayableMediaUrl(me.avatar_url);
  const showAvatarImg = avatarUrl != null;
  const initial = (me.full_name?.trim() || me.email || '?')[0]?.toUpperCase() ?? '?';
  const currentOrgLabel =
    me.memberships?.find((m) => m.organization_id === me.organization_id)?.organization_name ||
    orgSlug ||
    'организации';
  const contextLabel = me.context?.label?.trim() || null;
  const isPlatformTrainer =
    me.context?.surface === 'trainer' &&
    (me.role === 'superadmin' || me.role === 'platform_staff');

  return (
    <div className="space-y-5 w-full min-w-0">
      <h1 className="text-xl font-semibold tracking-tight">Профиль</h1>

      <section className="overflow-hidden rounded-2xl border border-[var(--border)] bg-[var(--bg-elevated)] p-4 shadow-[var(--shadow-card)] sm:p-5">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
          <div className="flex shrink-0 flex-col items-center gap-2 sm:items-start">
            <label
              htmlFor={avatarInputId}
              className="relative flex h-24 w-24 cursor-pointer items-center justify-center overflow-hidden rounded-full bg-[var(--bg-soft)] text-2xl font-semibold text-[var(--accent)] ring-2 ring-[color-mix(in_srgb,var(--accent)_22%,transparent)] focus-within:outline-none focus-within:ring-2 focus-within:ring-[var(--focus-ring)] active:opacity-90"
              aria-label="Загрузить или сменить фото"
            >
              <input
                id={avatarInputId}
                type="file"
                accept="image/*"
                className="sr-only"
                onChange={onAvatarFile}
                disabled={avatarBusy}
              />
              {showAvatarImg ? (
                <img src={avatarUrl} alt="" className="pointer-events-none h-full w-full object-cover" />
              ) : (
                <span aria-hidden className="pointer-events-none">
                  {initial}
                </span>
              )}
              {avatarBusy ? (
                <span className="absolute inset-0 flex items-center justify-center rounded-full bg-black/45 text-xs font-medium text-white">
                  …
                </span>
              ) : null}
            </label>
            {showAvatarImg ? (
              <Button
                type="button"
                variant="ghost"
                className="text-xs"
                disabled={avatarBusy}
                onClick={async () => {
                  setAvatarErr(null);
                  setAvatarBusy(true);
                  try {
                    await patchMeProfile({ avatar_url: null }, accessToken);
                    await reloadMe();
                  } catch (e) {
                    const msg = e instanceof ApiError ? e.detail : e instanceof Error ? e.message : 'Не удалось удалить';
                    setAvatarErr(msg);
                  } finally {
                    setAvatarBusy(false);
                  }
                }}
              >
                Удалить фото
              </Button>
            ) : null}
            {avatarErr ? (
              <EmbedMessageBlock variant="error" size="field" className="max-w-[12rem]" role="alert">
                {avatarErr}
              </EmbedMessageBlock>
            ) : null}
          </div>

          <dl className="grid min-w-0 flex-1 gap-3 text-sm sm:grid-cols-2">
            <div className="flex flex-wrap items-start justify-between gap-2 sm:col-span-2">
              <div className="min-w-0">
                <dt className="text-[13px] text-[var(--text-muted)]">Имя</dt>
                <dd className="mt-0.5 text-[var(--text)]">{me.full_name?.trim() ? me.full_name : '—'}</dd>
              </div>
              <button
                type="button"
                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[var(--text-muted)] hover:bg-[var(--bg-soft)] hover:text-[var(--text)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]"
                onClick={openNameDialog}
                aria-label="Изменить имя"
              >
                <IconPencil />
              </button>
            </div>
            <div>
              <dt className="text-[13px] text-[var(--text-muted)]">Электронная почта</dt>
              <dd className="mt-0.5 break-all text-[var(--text)]">{me.email}</dd>
            </div>
            <div>
              <dt className="text-[13px] text-[var(--text-muted)]">Роль</dt>
              <dd className="mt-0.5 text-[var(--text)]">
                {isOrgStaffLineWithRuntime(me) ? 'Сотрудник (тренерский кабинет)' : me.role}
              </dd>
            </div>
            <div>
              <dt className="text-[13px] text-[var(--text-muted)]">Филиал для работы</dt>
              {allOrgBranches ? (
                <div className="mt-2 flex flex-col gap-2">
                  {branchOptions.map((b) => (
                    <div
                      key={b.id}
                      className="flex h-10 w-full items-center rounded-lg border border-[var(--border)] bg-[var(--bg-elevated)] px-3 text-sm text-[var(--text)]"
                    >
                      {b.name}
                    </div>
                  ))}
                </div>
              ) : branchOptions.length > 0 ? (
                <div className="mt-2 flex flex-col gap-2">
                  {branchOptions.map((p) => {
                    const isActive = me.active_branch_id === p.id;
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => void chooseBranch(p.id)}
                        disabled={branchBusy}
                        className={`flex h-10 w-full items-center justify-between rounded-lg border px-3 text-sm transition-all active:scale-[0.98] ${
                          isActive
                            ? 'border-[var(--accent)] bg-[color-mix(in_srgb,var(--accent)_10%,var(--bg-elevated))] text-[var(--accent)] font-semibold'
                            : 'border-[var(--border)] bg-[var(--bg-elevated)] text-[var(--text)] hover:bg-[var(--bg-soft)]'
                        }`}
                      >
                        <span>{p.name}</span>
                        {isActive && (
                          <svg className="h-4 w-4 text-[var(--accent)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </button>
                    );
                  })}
                  {branchBusy && <p className="text-xs text-[var(--text-muted)] mt-1">Сохраняем…</p>}
                  {branchErr && <p className="text-xs text-[var(--error-text)] mt-1">{branchErr}</p>}
                </div>
              ) : null}
            </div>
            <div className="sm:col-span-2">
              <dt className="text-[13px] text-[var(--text-muted)]">Доступные филиалы</dt>
              <dd className="mt-0.5 text-[var(--text)]">{branchOptions.length > 0 ? `${branchOptions.length}` : '—'}</dd>
            </div>
          </dl>
        </div>
      </section>

      {contextLabel ? (
        <section className="rounded-2xl border border-[var(--border)] bg-[var(--bg-elevated)] p-4 shadow-[var(--shadow-card)] sm:p-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-[13px] text-[var(--text-muted)]">Вы вошли как</p>
              <p className="mt-0.5 text-base font-semibold text-[var(--text)]">{contextLabel}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {me.has_other_contexts ? (
                <Button type="button" variant="secondary" className="min-h-9" onClick={() => void openContexts()}>
                  Сменить роль
                </Button>
              ) : null}
              {isPlatformTrainer ? (
                <Button
                  type="button"
                  variant="secondary"
                  className="min-h-9"
                  onClick={() => navigate(`${PATH.trainerSelectStudio}${suffix}`)}
                >
                  Сменить студию
                </Button>
              ) : null}
            </div>
          </div>
        </section>
      ) : null}

      <TelegramLinkCard
        accessToken={accessToken}
        branchId={me.active_branch_id}
        telegramId={me.telegram_id}
        notifyViaTelegram={me.notify_via_telegram}
        onReload={() => reloadMe()}
        title="Telegram тренера"
      />

      <p className="text-xs leading-relaxed text-[var(--text-muted)]">
        Смена email для учётной записи сотрудника выполняется администратором организации. Пароль можно обновить через{' '}
        <Link className="text-[var(--accent)] underline-offset-2 hover:underline" to={`${PATH.forgotPassword}${suffix}`}>
          восстановление доступа
        </Link>
        .
      </p>

      {widgetContour === 'trainer-mobile' ? <MobileLegalLinks /> : null}

      {me.organization_id ? (
        <div className="pt-2">
          <button
            type="button"
            className="inline-flex items-center gap-1.5 rounded-xl px-4 py-2.5 text-sm font-medium text-[var(--error-text)] transition-colors hover:bg-[var(--error-bg)]"
            onClick={() => setLeaveOpen(true)}
          >
            Выйти из организации
          </button>
        </div>
      ) : null}

      <div className="pt-2">
        <button
          type="button"
          className="inline-flex items-center gap-1.5 rounded-xl px-4 py-2.5 text-sm font-medium text-[var(--error-text)] transition-colors hover:bg-[var(--error-bg)]"
          onClick={() => clearEmbedSession(orgSlug)}
        >
          Выйти из аккаунта
          <span className="text-base leading-none opacity-60">→</span>
        </button>
      </div>

      <div className="pt-2">
        <div className="inline-block rounded-xl border-2 border-red-300 bg-red-50/50 dark:border-red-800 dark:bg-red-950/20">
          <button
            type="button"
            className="flex w-full items-center gap-1.5 rounded-xl px-4 py-2.5 text-sm font-semibold text-red-600 transition-colors hover:bg-red-100 dark:text-red-400 dark:hover:bg-red-950/40"
            onClick={() => setDeleteOpen(true)}
          >
            Удалить аккаунт
          </button>
        </div>
        <p className="mt-2 max-w-md text-xs leading-relaxed text-[var(--text-muted)]">
          Профиль и данные удаляются безвозвратно; история бронирований у организации сохраняется в обезличенном виде.
        </p>
      </div>

      {cropSrc && cropOpen ? (
        <ImageCropModal
          open
          imageSrc={cropSrc}
          onOpenChange={(o) => {
            if (!o) closeCrop();
          }}
          title="Фото профиля"
          hint="Квадрат 1:1. После обрезки фото сохранится в профиле."
          outputLimits={{ maxOutputWidth: 512, maxOutputHeight: 512 }}
          onApplyError={(m) => setAvatarErr(m)}
          onApply={async (file) => {
            setAvatarErr(null);
            setAvatarBusy(true);
            try {
              const res = await uploadMediaWithToken(file, accessToken, { imageProfile: 'square' });
              const url = res?.url;
              if (!url) return;
              await patchMeProfile({ avatar_url: url }, accessToken);
              await reloadMe();
            } catch (e) {
              const msg = e instanceof ApiError ? e.detail : e instanceof Error ? e.message : 'Ошибка загрузки';
              setAvatarErr(msg);
              throw e;
            } finally {
              setAvatarBusy(false);
            }
          }}
        />
      ) : null}

      {nameOpen ? (
        <div
          className="fixed inset-0 z-[110] flex items-center justify-center bg-black/55 p-4"
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget && !nameSaving) setNameOpen(false);
          }}
        >
          <div className="w-full max-w-md rounded-2xl border border-[var(--border)] bg-[var(--bg-elevated)] p-5 shadow-lg" role="dialog" aria-modal="true" aria-labelledby="trainer-name-dialog-title">
            <h2 id="trainer-name-dialog-title" className="text-lg font-semibold text-[var(--text)]">
              Имя в профиле
            </h2>
            <p className="mt-1 text-sm text-[var(--text-muted)]">Отображается клиентам в тренерском сценарии.</p>
            <EmbedInput id={nameFieldId} className="mt-3" value={nameDraft} onChange={(e) => setNameDraft(e.target.value)} autoComplete="name" />
            {nameErr ? (
              <EmbedMessageBlock className="mt-2 w-full" variant="error" size="field" role="alert">
                {nameErr}
              </EmbedMessageBlock>
            ) : null}
            <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Button type="button" variant="secondary" disabled={nameSaving} onClick={() => setNameOpen(false)}>
                Отмена
              </Button>
              <Button
                type="button"
                disabled={nameSaving}
                onClick={async () => {
                  const v = nameDraft.trim();
                  if (!v) {
                    setNameErr('Укажите имя.');
                    return;
                  }
                  setNameErr(null);
                  setNameSaving(true);
                  try {
                    await patchMeProfile({ full_name: v }, accessToken);
                    await reloadMe();
                    setNameOpen(false);
                  } catch (e) {
                    const msg = e instanceof ApiError ? e.detail : e instanceof Error ? e.message : 'Не сохранилось';
                    setNameErr(msg);
                  } finally {
                    setNameSaving(false);
                  }
                }}
              >
                {nameSaving ? 'Сохраняем…' : 'Сохранить'}
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {contextsOpen ? (
        <div
          className="fixed inset-0 z-[110] flex items-center justify-center bg-black/55 p-4"
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget && !contextSwitchId) setContextsOpen(false);
          }}
        >
          <div
            className="w-full max-w-md rounded-2xl border border-[var(--border)] bg-[var(--bg-elevated)] p-5 shadow-lg"
            role="dialog"
            aria-modal="true"
            aria-labelledby="trainer-context-dialog-title"
          >
            <h2 id="trainer-context-dialog-title" className="text-lg font-semibold text-[var(--text)]">
              Сменить роль
            </h2>
            <p className="mt-1 text-sm text-[var(--text-muted)]">Выберите, кем продолжить работу.</p>
            {contextsLoading ? (
              <p className="mt-3 text-sm text-[var(--text-muted)]">Загружаем роли…</p>
            ) : contexts.length === 0 ? (
              <p className="mt-3 text-sm text-[var(--text-muted)]">Других ролей нет.</p>
            ) : (
              <div className="mt-3 space-y-2">
                {contexts.map((context) => (
                  <button
                    key={context.id}
                    type="button"
                    data-testid={`trainer-switch-context-${context.id}`}
                    disabled={contextSwitchId !== null}
                    onClick={() => void switchToContext(context)}
                    className="flex min-h-11 w-full items-center justify-between rounded-xl border border-[var(--border)] bg-[var(--bg-elevated)] px-3 py-2 text-left text-sm text-[var(--text)] transition-colors hover:bg-[var(--bg-soft)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)] disabled:opacity-60"
                  >
                    <span>{context.label}</span>
                    <span className="text-[var(--text-muted)]">{contextSwitchId === context.id ? 'Входим…' : '→'}</span>
                  </button>
                ))}
              </div>
            )}
            {contextsError ? (
              <EmbedMessageBlock variant="error" size="field" className="mt-3 w-full" role="alert">
                {contextsError}
              </EmbedMessageBlock>
            ) : null}
            <div className="mt-4 flex justify-end">
              <Button type="button" variant="secondary" disabled={contextSwitchId !== null} onClick={() => setContextsOpen(false)}>
                Закрыть
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      <DeleteAccountDialog
        open={deleteOpen}
        accessToken={accessToken}
        organizations={Array.from(
          new Set(
            (me.memberships ?? [])
              .map((m) => (m.organization_name || m.organization_slug || '').trim())
              .filter((n) => n !== '')
          )
        )}
        onDeleted={handleAccountDeleted}
        onCancel={() => setDeleteOpen(false)}
      />

      {me.organization_id ? (
        <LeaveOrganizationDialog
          open={leaveOpen}
          accessToken={accessToken}
          organizationId={me.organization_id}
          organizationName={currentOrgLabel}
          onLeft={() => {
            setLeaveOpen(false);
            void reloadMe();
          }}
          onCancel={() => setLeaveOpen(false)}
        />
      ) : null}
    </div>
  );
}
